import React, { useEffect } from 'react'
import { FormProvider, useForm, useFormContext, type DefaultValues, type FieldValues, type Resolver } from 'react-hook-form'

import { OrderItemsModuleProvider, useOrderItemsModule } from './contexts/OrderItemsModuleContext'
import { ORDER_SECTION_IDS } from './constants'
import Details from './Sections/Details'
import MainInfos, { SelectSource } from './Sections/MainInfos'
import Payment from './Sections/Payment'
import Review from './Sections/Review'
import Shipping from './Sections/Shipping'
import { Card } from './styles/shared'
import type { OrderItemsModule } from './types'

export interface OrderFormProps<TValues extends FieldValues> {
  resolver: Resolver<TValues>
  defaultValues: DefaultValues<TValues>
  /** valores do pedido carregado; chegam depois do primeiro render e reidratam o form */
  loadedValues?: TValues
  itemsModule: OrderItemsModule<any, any>
  supplier: SelectSource
  seller: SelectSource
  client: SelectSource
  /** o aço mantém o submit nativo aberto; o fashion nunca teve. Roteia pela mesma guarda. */
  nativeSubmit?: { onValid: (data: TValues) => void; onInvalid?: () => void; onGuardFailure?: () => void }
  contactSlot?: React.ReactNode
  attachmentsSlot?: React.ReactNode
  /** aço: Observações para produção do pedido, ao lado de "Informação extra" */
  productionObservationSlot?: React.ReactNode
  shippingValueSlot?: React.ReactNode
  carrierSlot?: React.ReactNode
  deadlineSlot?: React.ReactNode
  shippingExtras?: React.ReactNode
  taxationSlot?: React.ReactNode
  methodDescriptionSlot?: React.ReactNode
  installmentsSlot?: React.ReactNode
  stepper: React.ReactNode
  /** pontes e modais do tenant que precisam do form e do módulo */
  children?: React.ReactNode
}

/**
 * Dono do formulário de pedido: cria o form, monta os providers e compõe as seções na ordem que os
 * dois tenants compartilham. O que diverge entra por slot; o módulo de itens traz o próprio
 * provider quando precisa de um.
 */
function OrderForm<TValues extends FieldValues>({
  resolver, defaultValues, loadedValues, itemsModule,
  supplier, seller, client, nativeSubmit,
  contactSlot, attachmentsSlot, productionObservationSlot,
  shippingValueSlot, carrierSlot, deadlineSlot, shippingExtras, taxationSlot,
  methodDescriptionSlot, installmentsSlot, stepper, children,
}: OrderFormProps<TValues>) {
  const methods = useForm<TValues>({ resolver, defaultValues, mode: 'onBlur' })
  const { reset } = methods

  useEffect(() => {
    if (loadedValues) reset(loadedValues)
  }, [loadedValues, reset])

  const ItemsSlot = itemsModule.Slot
  const ModuleOwnProvider = itemsModule.Provider ?? React.Fragment

  const tree = (
    <ModuleOwnProvider>
      <OrderItemsModuleProvider module={itemsModule}>
        <NativeSubmitBoundary nativeSubmit={nativeSubmit}>
        {children}
        {stepper}

        <div id={ORDER_SECTION_IDS.main}>
          <Card>
            <MainInfos supplier={supplier} seller={seller} client={client}>
              {contactSlot}
            </MainInfos>
            <Details attachmentsSlot={attachmentsSlot} productionObservationSlot={productionObservationSlot} />
          </Card>
        </div>

        <div id={ORDER_SECTION_IDS.items}>
          <ItemsSlot />
        </div>

        {/* Cartões separados, âncora compartilhada: a tributação respira como seção própria, mas o
            stepper continua tratando as duas como "Revisão" — ela não tem passo nem título. */}
        <div id={ORDER_SECTION_IDS.review}>
          <Card>
            <Review />
          </Card>
          {taxationSlot && <Card>{taxationSlot}</Card>}
        </div>

        <div id={ORDER_SECTION_IDS.shipping}>
          <Card>
            <Shipping
              valueSlot={shippingValueSlot}
              carrierSlot={carrierSlot}
              deadlineSlot={deadlineSlot}
            >
              {shippingExtras}
            </Shipping>
          </Card>
        </div>

        <div id={ORDER_SECTION_IDS.payment}>
          <Card>
            <Payment methodDescriptionSlot={methodDescriptionSlot}>
              {installmentsSlot}
            </Payment>
          </Card>
        </div>
        </NativeSubmitBoundary>
      </OrderItemsModuleProvider>
    </ModuleOwnProvider>
  )

  return <FormProvider {...methods}>{tree}</FormProvider>
}

/**
 * O aço tem um `<form>` de verdade e o Enter dispara submit por ele. Precisa passar pela mesma
 * guarda de itens do botão, senão os dois caminhos divergem — um salva sem itens, o outro não.
 */
function NativeSubmitBoundary<TValues extends FieldValues>({
  nativeSubmit, children,
}: { nativeSubmit?: OrderFormProps<TValues>['nativeSubmit']; children: React.ReactNode }) {
  const { handleSubmit } = useFormContext<TValues>()
  const { validateBeforeSave } = useOrderItemsModule()

  if (!nativeSubmit) return <>{children}</>

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!validateBeforeSave().ok) {
      nativeSubmit.onGuardFailure?.()
      return
    }
    handleSubmit(nativeSubmit.onValid as any, nativeSubmit.onInvalid)()
  }

  return <form onSubmit={onSubmit}>{children}</form>
}

export default OrderForm
